class PWAService {
  private updateAvailable = false;
  private registration: ServiceWorkerRegistration | null = null;

  async initialize() {
    if ('serviceWorker' in navigator) {
      try {
        this.registration = await navigator.serviceWorker.register('/sw.js');
        
        this.registration.addEventListener('updatefound', () => {
          const newWorker = this.registration?.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                this.updateAvailable = true;
                this.notifyUpdateAvailable();
              }
            });
          }
        });

        navigator.serviceWorker.addEventListener('controllerchange', () => {
          window.location.reload();
        });

      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    }
  }

  private notifyUpdateAvailable() {
    // Dispatch a custom event that components can listen to
    window.dispatchEvent(new CustomEvent('pwa-update-available'));
  }

  async updateApp() {
    if (this.registration?.waiting) {
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }

  async getCacheSize(): Promise<number> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return estimate.usage || 0;
    }
    return 0;
  }

  async clearCache() {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
    }
  }

  isUpdateAvailable(): boolean {
    return this.updateAvailable;
  }

  getInstallationStatus() {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isInWebAppiOS = (window.navigator as any).standalone === true;
    return isStandalone || isInWebAppiOS;
  }

  getRegistration(): ServiceWorkerRegistration | null {
    return this.registration;
  }

  async isServiceWorkerHealthy(): Promise<boolean> {
    if (!this.registration) return false;
    
    // Check if service worker is active
    if (!this.registration.active) return false;
    
    // Check if service worker is properly intercepting requests
    try {
      const controller = navigator.serviceWorker.controller;
      return controller !== null;
    } catch {
      return false;
    }
  }
}

export const pwaService = new PWAService();